import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const SITE_URL = 'https://kikuflow.com'
const LOCALES = ['en', 'zh-tw', 'zh-cn', 'ja'] as const

// html lang 與 hreflang 需要 BCP-47 寫法，與 i18n 內部使用的小寫代碼不同
const BCP47: Record<string, string> = { 'en': 'en', 'zh-tw': 'zh-TW', 'zh-cn': 'zh-CN', 'ja': 'ja' }
const OG_LOCALE: Record<string, string> = { 'en': 'en_US', 'zh-tw': 'zh_TW', 'zh-cn': 'zh_CN', 'ja': 'ja_JP' }

// 社群分享卡片用的橫幅圖。維持 1200×630 的 40:21 比例，但以 1720×903 輸出，
// 讓高解析螢幕上的卡片不會糊掉（og:image:width/height 須與實際檔案尺寸一致）
const OG_IMAGE = `${SITE_URL}/og-image.jpg`
// JSON-LD Organization 的識別用 logo，與分享卡片用途不同，不可共用
const LOGO_IMAGE = `${SITE_URL}/kikuflow-logo.png`

// Organization 的節點識別碼。定價頁的 SoftwareApplication 用 @id 參照它，
// 兩個節點因此被認定為同一個實體，且不必在該頁重複整段 Organization
const ORG_ID = `${SITE_URL}/#organization`

// Organization 的 sameAs：把散在各處的品牌提及綁定成同一個實體
const SAME_AS = [
  'https://www.linkedin.com/company/kikuflow/',
  'https://www.youtube.com/@kikuflow'
]

/**
 * 英文以根路徑為正規版本：/en/pricing 的 canonical 指向 /pricing。
 * 其他語言則以自身路徑為正規版本。
 */
const urlFor = (locale: string, path: string) =>
  locale === 'en'
    ? `${SITE_URL}${path}`
    : `${SITE_URL}/${locale}${path === '/' ? '' : path}`


/**
 * 結構化資料必須對應該頁畫面上實際看得到的內容，否則會被判定為 spam。
 * 因此 Organization 只掛首頁，SoftwareApplication 與 FAQPage 只掛定價頁
 * （價格與四題問答都在該頁渲染）。
 */
const organizationLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'kikuflow',
  url: SITE_URL,
  logo: LOGO_IMAGE,
  email: 'hello@kikuflow.com',
  sameAs: SAME_AS,
  parentOrganization: { '@type': 'Organization', name: 'SoftReach Co., Ltd.' },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1-1-9 Asakusabashi, Taito-ku',
    addressLocality: 'Tokyo',
    postalCode: '111-0053',
    addressCountry: 'JP'
  }
})

const softwareApplicationLd = (description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'kikuflow',
  url: SITE_URL,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description,
  publisher: { '@id': ORG_ID },
  // 只標主方案；陪跑服務是加購項目，混進來會讓 AI 講錯價格
  offers: {
    '@type': 'Offer',
    price: '15',
    priceCurrency: 'USD',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '15',
      priceCurrency: 'USD',
      unitText: 'user/month'
    }
  }
})

const faqPageLd = (t: (key: string) => string) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [1, 2, 3, 4].map(n => ({
    '@type': 'Question',
    name: t(`pages./pricing.faq.q${n}`),
    acceptedAnswer: { '@type': 'Answer', text: t(`pages./pricing.faq.a${n}`) }
  }))
})

/**
 * 對外靜態頁的 SEO head。在 PublicLayout 呼叫一次即涵蓋所有路由，
 * 內容取自 route.meta.seoKey 對應的 locale 檔 seo 區塊。
 */
export function useSeoHead() {
  const route = useRoute()
  const { t, locale } = useI18n()

  // 去掉語言前綴，得到與語言無關的正規路徑（/、/pricing …）
  const basePath = computed(() => {
    const lang = route.params.lang as string | undefined
    let path = route.path
    if (lang && (path === `/${lang}` || path.startsWith(`/${lang}/`))) {
      path = path.slice(lang.length + 1)
    }
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1)
    }
    return path || '/'
  })

  const seoKey = computed(() => route.meta.seoKey as string | undefined)
  const noindex = computed(() => route.meta.noindex === true)

  const title = computed(() =>
    seoKey.value ? `${t(`seo.${seoKey.value}.title`)} | kikuflow` : 'kikuflow'
  )
  const description = computed(() =>
    seoKey.value ? t(`seo.${seoKey.value}.description`) : t('meta.description')
  )
  const canonical = computed(() => urlFor(locale.value, basePath.value))

  const jsonLd = computed(() => {
    if (seoKey.value === 'home') return [organizationLd()]
    if (seoKey.value === 'pricing') return [softwareApplicationLd(description.value), faqPageLd(t)]
    return []
  })

  useHead(() => ({
    htmlAttrs: { lang: BCP47[locale.value] ?? 'en' },
    title: title.value,
    script: jsonLd.value.map(data => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(data)
    })),
    // noindex 頁不需要 canonical 與 hreflang，避免送出互相矛盾的訊號
    link: noindex.value
      ? []
      : [
          { rel: 'canonical', href: canonical.value },
          ...LOCALES.map(l => ({
            rel: 'alternate',
            hreflang: BCP47[l],
            href: urlFor(l, basePath.value)
          })),
          { rel: 'alternate', hreflang: 'x-default', href: urlFor('en', basePath.value) }
        ],
    meta: [
      { name: 'description', content: description.value },
      ...(noindex.value ? [{ name: 'robots', content: 'noindex' }] : []),
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'kikuflow' },
      { property: 'og:title', content: title.value },
      { property: 'og:description', content: description.value },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: OG_IMAGE },
      { property: 'og:image:width', content: '1720' },
      { property: 'og:image:height', content: '903' },
      { property: 'og:locale', content: OG_LOCALE[locale.value] ?? 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title.value },
      { name: 'twitter:description', content: description.value },
      { name: 'twitter:image', content: OG_IMAGE }
    ]
  }))
}
